import { useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';

/**
 * Custom hook to track pageviews
 * - Buffers pageviews in memory (batching)
 * - Flushes to API periodically or when buffer is full
 * - Handles auth and anonymous users
 * - Deduplicates views within same session
 * 
 * Usage:
 * usePageviewTracker({
 *   contentId: '507f1f77bcf86cd799439011',
 *   contentType: 'content',
 *   contentTypeCategory: 'article'
 * });
 */
export function usePageviewTracker(options = {}) {
  const { user } = useAuth();
  const bufferRef = useRef([]);
  const timerRef = useRef(null);
  const sessionIdRef = useRef(null);
  const viewStartTimeRef = useRef(Date.now());

  // Initialize session ID (for deduplication)
  useEffect(() => {
    if (!sessionIdRef.current) {
      const stored = sessionStorage.getItem('qdn_session_id');
      if (stored) {
        sessionIdRef.current = stored;
      } else {
        // Generate new session ID
        sessionIdRef.current = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        sessionStorage.setItem('qdn_session_id', sessionIdRef.current);
      }
    }
  }, []);

  // Flush buffer to API
  const flushBuffer = useCallback(async () => {
    if (bufferRef.current.length === 0) {
      return;
    }

    const pageviewsToSend = [...bufferRef.current];
    bufferRef.current = [];

    try {
      const baseUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${baseUrl}/api/pageview/log`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-Id': sessionIdRef.current || '',
          ...(user && user.token ? { 'Authorization': `Bearer ${user.token}` } : {})
        },
        body: JSON.stringify({ pageviews: pageviewsToSend })
      });

      if (!response.ok) {
        console.warn(`Pageview logging failed: ${response.status}`);
        // Re-add to buffer for retry (but limit retries)
        bufferRef.current = pageviewsToSend.concat(bufferRef.current);
      } else {
        const data = await response.json();
        console.debug(`Pageviews logged: ${data.logged}, skipped: ${data.skipped}`);
      }
    } catch (error) {
      console.error('Error logging pageviews:', error);
      // Re-add to buffer for retry (but limit retries)
      bufferRef.current = pageviewsToSend.concat(bufferRef.current);
    }
  }, [user]);

  // Schedule auto-flush
  const scheduleFlush = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    // Auto-flush after 30 seconds of inactivity
    timerRef.current = setTimeout(() => {
      flushBuffer();
    }, 30000);
  }, [flushBuffer]);

  // Track pageview
  useEffect(() => {
    if (!options.contentId) {
      console.warn('usePageviewTracker: contentId is required');
      return;
    }

    // Capture ref values at effect start to avoid stale closure warnings
    const viewStartTime = viewStartTimeRef.current;

    const contentType = options.contentType || 'content';
    const contentTypeCategory = options.contentTypeCategory || 'article';

    // Create pageview record
    const pageview = {
      contentId: options.contentId,
      contentType: contentType,
      contentTypeCategory: contentTypeCategory,
      viewDuration: null
    };

    // Add to buffer
    bufferRef.current.push(pageview);
    console.debug(`Added pageview to buffer: ${options.contentId} (buffer size: ${bufferRef.current.length})`);

    // Check if buffer is full (flush if 10+ pageviews or buffer has mixed content types)
    if (bufferRef.current.length >= 10) {
      flushBuffer();
    } else {
      // Schedule auto-flush
      scheduleFlush();
    }

    // Cleanup on unmount: calculate view duration and flush
    return () => {
      const viewDuration = Date.now() - viewStartTime;
      
      // Update the last pageview with view duration
      if (bufferRef.current.length > 0) {
        bufferRef.current[bufferRef.current.length - 1].viewDuration = viewDuration;
      }

      // Force flush on unmount if buffer has items
      if (bufferRef.current.length > 0) {
        // Cancel auto-flush timer
        if (timerRef.current) {
          clearTimeout(timerRef.current);
        }

        // Flush immediately
        flushBuffer();
      }
    };
  }, [options.contentId, options.contentType, options.contentTypeCategory, flushBuffer, scheduleFlush]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);
}
